import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { useCheckoutDialog } from '../../contexts/CheckoutDialogContext';
import LazyCheckoutDialog from '../CheckoutDialog/LazyCheckoutDialog';
import LoginConfirmationDialog from '../LoginConfirmationDialog/LoginConfirmationDialog';
import OptimizedLottie from '../LazyLottie/OptimizedLottie';
import emptyBoxAnimation from '@assets/emptybox.json';
import config from '../../config/config.json';
import { logger } from '../../utils/logger';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showAuthDialog } = useAuthDialog();
  const { isCheckoutDialogOpen, setIsCheckoutDialogOpen } = useCheckoutDialog();
  const {
    items,
    updateQuantity,
    removeFromCart,
    getTotalPrice,
    refreshCart,
    isCartOperationInProgress,
    isItemBeingProcessed,
    isRefreshing,
  } = useCart();
  // const [showMinAmountDialog, setShowMinAmountDialog] = useState(false);
  const [showLoginConfirmation, setShowLoginConfirmation] = useState(false);
  const [loadingItems, setLoadingItems] = useState<
    Record<string, { decrease: boolean; increase: boolean; remove: boolean }>
  >({});
  const [optimisticItems, setOptimisticItems] = useState(items);
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Helper function to format currency with proper validation
  const formatCurrency = (
    amount: number,
    currency?:
      | {
          _id: string;
          code: string;
          name: string;
          symbol: string;
          _nameMultilingual?: {
            en?: string;
            my?: string;
          };
        }
      | string
      | null,
  ) => {
    // Handle new currency object structure
    if (currency && typeof currency === 'object') {
      const symbol = currency.symbol || currency.code;
      if (currency.code === 'USD') {
        return `${amount.toFixed(2)} ${symbol}`;
      }
      return `${amount.toLocaleString()} ${symbol}`;
    }

    // Fallback to MMK
    return `${amount.toLocaleString()} MMK`;
  };

  // Sync optimistic items with actual cart items
  useEffect(() => {
    setOptimisticItems(items);
  }, [items]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    const timeouts = debounceTimeouts.current;
    return () => {
      Object.values(timeouts).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Check for items with null/missing images and refresh cart if needed
  useEffect(() => {
    const hasItemsWithMissingImages = items.some(
      (item) => !item.image || item.image === '/placeholder-image.jpg' || item.image === null,
    );

    if (hasItemsWithMissingImages && items.length > 0) {
      logger.log('Found items with missing images, refreshing cart...');
      refreshCart().catch((error) => {
        logger.warn('Failed to refresh cart for missing images:', error);
      });
    }
  }, [items, refreshCart]);

  // Optimized quantity handlers with debouncing
  const handleQuantityDecrease = useCallback(
    (itemId: string, currentQuantity: number) => {
      return async () => {
        if (currentQuantity <= config.cart.minQuantityPerItem) return;

        // Clear existing timeout
        if (debounceTimeouts.current[itemId]) {
          clearTimeout(debounceTimeouts.current[itemId]);
        }

        // Optimistic update
        setOptimisticItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, quantity: Math.max(config.cart.minQuantityPerItem, item.quantity - 1) }
              : item,
          ),
        );

        setLoadingItems((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], decrease: true },
        }));

        // Debounced API call
        debounceTimeouts.current[itemId] = setTimeout(async () => {
          try {
            await updateQuantity(itemId, currentQuantity - 1);
          } catch (error) {
            logger.error('Failed to update quantity:', error);
            // Revert optimistic update on error
            setOptimisticItems(items);
          } finally {
            setLoadingItems((prev) => ({
              ...prev,
              [itemId]: { ...prev[itemId], decrease: false },
            }));
          }
        }, 300);
      };
    },
    [updateQuantity, items],
  );

  const handleQuantityIncrease = useCallback(
    (itemId: string, currentQuantity: number) => {
      return async () => {
        if (currentQuantity >= config.cart.maxQuantityPerItem) return;

        // Clear existing timeout
        if (debounceTimeouts.current[itemId]) {
          clearTimeout(debounceTimeouts.current[itemId]);
        }

        // Optimistic update
        setOptimisticItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, quantity: Math.min(config.cart.maxQuantityPerItem, item.quantity + 1) }
              : item,
          ),
        );

        setLoadingItems((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], increase: true },
        }));

        // Debounced API call
        debounceTimeouts.current[itemId] = setTimeout(async () => {
          try {
            await updateQuantity(itemId, currentQuantity + 1);
          } catch (error) {
            logger.error('Failed to update quantity:', error);
            // Revert optimistic update on error
            setOptimisticItems(items);
          } finally {
            setLoadingItems((prev) => ({
              ...prev,
              [itemId]: { ...prev[itemId], increase: false },
            }));
          }
        }, 300);
      };
    },
    [updateQuantity, items],
  );

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      return async () => {
        setLoadingItems((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], remove: true },
        }));

        // Optimistic update - remove item immediately from UI
        setOptimisticItems((prev) => prev.filter((item) => item.id !== itemId));

        try {
          await removeFromCart(itemId);
        } catch (error) {
          logger.error('Failed to remove from cart:', error);
          // Revert optimistic update on error
          setOptimisticItems(items);
        } finally {
          setLoadingItems((prev) => ({
            ...prev,
            [itemId]: { ...prev[itemId], remove: false },
          }));
        }
      };
    },
    [removeFromCart, items],
  );

  return (
    <>
      {createPortal(
        <div
          className={`fixed inset-0 z-[9999] transition-all duration-300 ease-out will-change-transform transform-gpu
                      ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        >
          {/* Backdrop with blur effect */}
          <div
            className={`absolute inset-0 backdrop-blur-sm bg-black/30 transition-opacity duration-300
                            ${isOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={onClose}
          />

          {/* Drawer Container */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center p-4">
              {/* Drawer Panel */}
              <div
                className={`bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl w-full md:w-96 rounded-2xl transform transition-all duration-300 ease-out
                                ${
                                  isOpen
                                    ? 'scale-100 opacity-100 translate-y-0'
                                    : 'scale-95 opacity-0 translate-y-8'
                                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">{t('cart')}</h2>
                    {items.length > 0 && (
                      <span className="bg-blue-100 text-blue-600 text-sm px-2 py-0.5 rounded-full">
                        {items.reduce((total, item) => total + item.quantity, 0)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={refreshCart}
                      disabled={isRefreshing}
                      className={`p-2 rounded-full transition-colors ${
                        isRefreshing ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-gray-100'
                      }`}
                      title={t('titles.refresh_cart')}
                    >
                      {isRefreshing ? (
                        <svg
                          className="w-5 h-5 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col h-96">
                  {/* Cart Items */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {items.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="relative z-20 flex-1 flex items-center justify-center">
                          <OptimizedLottie
                            animationData={emptyBoxAnimation}
                            className="w-3/4 h-auto mx-auto"
                            loop={true}
                            autoplay={true}
                            lazy={false}
                            priority="high"
                          />
                        </div>

                        <p className="text-gray-500">{t('cart_empty')}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {items.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg"
                          >
                            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                              {item.image && item.image !== '/placeholder-image.jpg' ? (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="w-full h-full object-cover rounded-lg"
                                  onError={async (e) => {
                                    logger.log('Image failed to load:', item.image);
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    // Refresh cart to get fresh data if image fails
                                    try {
                                      await refreshCart();
                                    } catch (error) {
                                      logger.warn(
                                        'Failed to refresh cart after image error:',
                                        error,
                                      );
                                    }
                                  }}
                                />
                              ) : null}
                              <svg
                                className={`w-6 h-6 text-gray-400 ${item.image && item.image !== '/placeholder-image.jpg' ? 'hidden' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            </div>

                            <div className="flex-1">
                              <h3 className="font-medium text-sm">{item.title}</h3>
                              <div className="text-sm">
                                {/* Price Display */}
                                <div className="flex items-center gap-2">
                                  {item.discountPrice && item.discountPrice < (item.price || 0) ? (
                                    <>
                                      <span className="text-gray-500 line-through text-xs">
                                        {item.price || 0}
                                      </span>
                                      <span className="font-bold text-gray-800">
                                        {formatCurrency(item.discountPrice, item.currency)}
                                      </span>
                                      {item.commissionRate ? (
                                        <span className="bg-green-100 text-green-600 px-1.5 py-0.5 rounded text-xs font-medium">
                                          {item.discountType === 'percentage'
                                            ? `+${item.discountValue}%`
                                            : `+${formatCurrency((item.price || 0) - item.discountPrice, item.currency)}`}
                                        </span>
                                      ) : (
                                        <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded text-xs font-medium">
                                          {item.discountType === 'percentage'
                                            ? `-${item.discountValue}%`
                                            : `-${formatCurrency((item.price || 0) - item.discountPrice, item.currency)}`}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="font-bold text-gray-800">
                                      {formatCurrency(item.price || 0, item.currency)}
                                    </span>
                                  )}
                                </div>

                                {/* Commission Rate Info for Merchant */}
                                {item.commissionRate && item.commissionRate > 0 ? (
                                  <div className="text-xs text-gray-500">
                                    Commission
                                    <span className="ml-2 bg-green-100 text-green-600 px-1.5 py-0.5 rounded text-xs font-bold">
                                      {`${item.commissionRate}%`}
                                    </span>
                                  </div>
                                ) : null}

                                {/* Exchange Rate Info for Gift Cards */}
                                {(item.type === 'gift card' || item.type === 'giftcard') &&
                                  item.fx?.productCurrencySymbol &&
                                  item.fx.productCurrencySymbol !== item.fx.baseCurrencySymbol && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      (1 {item.fx.productCurrencySymbol} = {item.fx.exchangeRate}{' '}
                                      {item.fx.baseCurrencySymbol})
                                    </div>
                                  )}
                              </div>

                              {/* Quantity Controls */}
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex items-center bg-white rounded-lg">
                                  <button
                                    onClick={handleQuantityDecrease(item.id, item.quantity)}
                                    disabled={
                                      item.quantity <= config.cart.minQuantityPerItem ||
                                      loadingItems[item.id]?.decrease ||
                                      (isCartOperationInProgress() &&
                                        !isItemBeingProcessed(item.id))
                                    }
                                    className={`p-1 ml-0.5 rounded-l-lg ${
                                      item.quantity <= config.cart.minQuantityPerItem ||
                                      loadingItems[item.id]?.decrease ||
                                      (isCartOperationInProgress() &&
                                        !isItemBeingProcessed(item.id))
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'hover:bg-gray-100'
                                    }`}
                                  >
                                    {loadingItems[item.id]?.decrease ? (
                                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M20 12H4"
                                        />
                                      </svg>
                                    )}
                                  </button>
                                  <span className="px-3 py-1 text-sm">
                                    {optimisticItems.find((opt) => opt.id === item.id)?.quantity ||
                                      item.quantity}
                                  </span>
                                  <button
                                    onClick={handleQuantityIncrease(item.id, item.quantity)}
                                    disabled={
                                      item.quantity >= config.cart.maxQuantityPerItem ||
                                      loadingItems[item.id]?.increase ||
                                      (isCartOperationInProgress() &&
                                        !isItemBeingProcessed(item.id))
                                    }
                                    className={`p-1 rounded-r-lg mr-0.5 ${
                                      item.quantity >= config.cart.maxQuantityPerItem ||
                                      loadingItems[item.id]?.increase ||
                                      (isCartOperationInProgress() &&
                                        !isItemBeingProcessed(item.id))
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'hover:bg-gray-100'
                                    }`}
                                  >
                                    {loadingItems[item.id]?.increase ? (
                                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                        />
                                      </svg>
                                    )}
                                  </button>
                                </div>

                                <button
                                  onClick={handleRemoveItem(item.id)}
                                  disabled={
                                    loadingItems[item.id]?.remove ||
                                    (isCartOperationInProgress() && !isItemBeingProcessed(item.id))
                                  }
                                  className={`p-1 rounded ${
                                    loadingItems[item.id]?.remove ||
                                    (isCartOperationInProgress() && !isItemBeingProcessed(item.id))
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-red-500 hover:bg-red-50'
                                  }`}
                                >
                                  {loadingItems[item.id]?.remove ? (
                                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <svg
                                      className="w-4 h-4"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {items.length > 0 && (
                    <div className="border-t p-4">
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-medium">{t('total')}</span>
                        <span className="text-lg font-bold text-blue-600">
                          {formatCurrency(getTotalPrice())}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          if (!user) {
                            setShowLoginConfirmation(true);
                          } else {
                            onClose();
                            setIsCheckoutDialogOpen(true);
                          }
                        }}
                        className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium
                                          hover:bg-blue-700 transform transition-all duration-200 hover:scale-[1.02]"
                      >
                        {t('checkout')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <LazyCheckoutDialog
        isOpen={isCheckoutDialogOpen}
        onClose={() => setIsCheckoutDialogOpen(false)}
      />

      {/* Minimum Amount Warning Dialog */}
      {/* {showMinAmountDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[90] animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform animate-in zoom-in-95 duration-300">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">
                {t('common.minimum_amount_required')}
              </h3>
              <p className="text-gray-600 mb-4">{t('common.minimum_amount_message')}</p>
              <button
                onClick={() => {
                  setShowMinAmountDialog(false);
                  onClose();
                }}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {showLoginConfirmation && (
        <LoginConfirmationDialog
          isOpen={showLoginConfirmation}
          onClose={() => setShowLoginConfirmation(false)}
          onLogin={() => {
            setShowLoginConfirmation(false);
            showAuthDialog('login');
          }}
          onContinueAsGuest={() => {
            setShowLoginConfirmation(false);
            onClose();
            setIsCheckoutDialogOpen(true);
          }}
        />
      )}
    </>
  );
}
